import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'

import * as dealsAPI from '../../../apis/dealsAPI'
import type {
  InvoiceEntity,
  VoidInvoiceReqBody,
} from '../../../apis/dealsAPI'
import { useApiErrorHandler } from '../../../hooks/useApiErrorHandler'
import { useToast } from '../../../hooks/useToast'

import { dealQueryKey } from './useDealQuery'
import { invoicesQueryKey } from './useInvoicesQuery'

export interface VoidInvoiceVariables {
  dealId: string
  invoiceId: string
  body?: VoidInvoiceReqBody
}

interface Snapshot {
  invoicesSnapshot: { key: readonly unknown[]; data: InvoiceEntity[] | undefined }
}

function patchInvoiceStatus(
  invoices: InvoiceEntity[],
  invoiceId: string,
  status: InvoiceEntity['status'],
): InvoiceEntity[] {
  return invoices.map((invoice) =>
    invoice.id === invoiceId ? { ...invoice, status } : invoice,
  )
}

export function useVoidInvoiceMutation(): UseMutationResult<
  InvoiceEntity,
  unknown,
  VoidInvoiceVariables,
  Snapshot
> {
  const queryClient = useQueryClient()
  const toast = useToast()
  const handleError = useApiErrorHandler()

  return useMutation<InvoiceEntity, unknown, VoidInvoiceVariables, Snapshot>(
    ({ dealId, invoiceId, body }) => dealsAPI.voidInvoice(dealId, invoiceId, body),
    {
      onMutate: async ({ dealId, invoiceId }): Promise<Snapshot> => {
        await queryClient.cancelQueries({ queryKey: invoicesQueryKey(dealId) })

        const invoicesKey = invoicesQueryKey(dealId)
        const invoicesData = queryClient.getQueryData<InvoiceEntity[]>(invoicesKey)
        if (invoicesData) {
          queryClient.setQueryData<InvoiceEntity[]>(
            invoicesKey,
            patchInvoiceStatus(invoicesData, invoiceId, 'Voided'),
          )
        }
        return { invoicesSnapshot: { key: invoicesKey, data: invoicesData } }
      },
      onError: (error, _vars, context) => {
        if (context) {
          queryClient.setQueryData(context.invoicesSnapshot.key, context.invoicesSnapshot.data)
        }
        handleError(error)
      },
      onSuccess: (invoice) => {
        toast.current?.show({
          severity: 'info',
          summary: 'Invoice voided',
          detail: `${invoice.invoiceNumber} moved to the voided collapse.`,
          life: 3000,
        })
      },
      onSettled: (_data, _err, { dealId }) => {
        queryClient.invalidateQueries({ queryKey: invoicesQueryKey(dealId) })
        queryClient.invalidateQueries({ queryKey: dealQueryKey(dealId) })
      },
    },
  )
}
